import React from 'react';
import { useAccountTx } from '@/src/hooks/apis';
import type { UseMarketActivityParams, MarketActivityRow } from '../market-activity.types';
import { isObject, isString } from '@/src/helpers';

export function useMarketActivity({
  baseToken,
  quoteToken,
  limit = 10,
}: UseMarketActivityParams) {
  // Use the proper XRPL query hook following the same pattern as other components
  const {
    data: accountTxData,
    isLoading,
    error,
  } = useAccountTx(
    {
      command: 'account_tx',
      account: baseToken?.issuer ?? '',
      limit: limit * 3, // Get more to filter down
    },
    {
      enabled: !!baseToken && !!quoteToken && baseToken.code !== 'XRP',
      staleTime: 1000 * 60, // 1 minute
      gcTime: 1000 * 60 * 5, // 5 minutes
      refetchInterval: 1000 * 60, // Refetch every minute
    }
  );

  // Transform the data into market transactions
  const transactions: MarketActivityRow[] = React.useMemo(() => {
    if (!accountTxData?.transactions || !baseToken || !quoteToken) {
      return [];
    }

    // Filter and transform transactions
    const marketTransactions: MarketActivityRow[] = accountTxData.transactions
      .filter((tx: any) => tx && tx.tx_json)
      .filter((tx: any) => {
        const transaction = tx.tx_json;
        // Focus on OfferCreate transactions that involve the token pair
        if (transaction.TransactionType !== 'OfferCreate') {
          return false;
        }

        const takerGets = transaction.TakerGets;
        const takerPays = transaction.TakerPays;

        // Check if this offer involves our token pair
        const involvesBaseToken = 
          (isObject(takerGets) && takerGets.currency === baseToken.code && takerGets.issuer === baseToken.issuer) ||
          (isObject(takerPays) && takerPays.currency === baseToken.code && takerPays.issuer === baseToken.issuer);

        const involvesQuoteToken = 
          (isObject(takerGets) && takerGets.currency === quoteToken.code && (quoteToken.code === 'XRP' || takerGets.issuer === quoteToken.issuer)) ||
          (isObject(takerPays) && takerPays.currency === quoteToken.code && (quoteToken.code === 'XRP' || takerPays.issuer === quoteToken.issuer));

        return involvesBaseToken && involvesQuoteToken;
      })
      .slice(0, limit) // Take only the requested number
      .map((tx: any) => {
        const transaction = tx.tx_json;
        const takerGets = transaction.TakerGets;
        const takerPays = transaction.TakerPays;
        
        // Determine if this is a buy or sell order
        // BUY: takerGets = quoteToken, takerPays = baseToken
        // SELL: takerGets = baseToken, takerPays = quoteToken
        // Since baseToken cannot be XRP (filtered out), we only check for object currency
        const takerGetsIsBaseToken = 
          isObject(takerGets) && 
          takerGets.currency === baseToken.code && 
          takerGets.issuer === baseToken.issuer;
        
        const isBuyOrder = !takerGetsIsBaseToken; // If takerGets is NOT baseToken, it's a buy
        
        const baseAmount = isBuyOrder ? 
          (isObject(takerPays) ? takerPays.value : (isString(takerPays) ? takerPays : '0')) :
          (isObject(takerGets) ? takerGets.value : (isString(takerGets) ? takerGets : '0'));
        
        const quoteAmount = isBuyOrder ? 
          (isObject(takerGets) ? takerGets.value : (isString(takerGets) ? takerGets : '0')) :
          (isObject(takerPays) ? takerPays.value : (isString(takerPays) ? takerPays : '0'));
        
        const price = parseFloat(quoteAmount) / parseFloat(baseAmount);
        
        return {
          hash: tx.hash || '',
          time: tx.close_time_iso || new Date().toISOString(),
          type: isBuyOrder ? 'buy' : 'sell',
          amount: baseAmount,
          price: price.toFixed(4),
          total: quoteAmount,
        };
      });

    return marketTransactions;
  }, [accountTxData, baseToken, quoteToken, limit]);

  return {
    data: transactions,
    isLoading,
    error,
  };
}
